import {Component, HostListener, OnDestroy, OnInit, ViewChild, ViewContainerRef} from "@angular/core";
import {filter, switchMap, tap} from "rxjs/operators";
import {NavigationStart, Router} from "@angular/router";
import {Constants, REDIRECT_URLS} from "./constants";
import {cartUxLocaleRoutes, rootAppRoutes} from "../app-routing.module";
import {SessionService} from "./common/session.service";
import {Util} from "./util/util";
import {combineLatest, Subscription} from "rxjs";
import {CartService} from "./cart/cart.service";
import {UserService} from "./common/user.service";
import {LocaleUtil} from "./util/locale-util";
import {ShareableUtil} from "./social/shareable-util";
import {CustomerCacheService} from "../checkout/customer/customer-cache.service";
import {WelcomeModalService} from "./welcome/welcome-modal.service";
import {IShopUser} from "./session/customer-models";
import {GqlShopSessionService} from "../checkout/graphql/shop-session/gql-shop-session.service";
import {NavigationService} from "./common/navigation.service";
import * as _ from "lodash";
import {LoggerService} from "./common/logger.service";
import {UsanaObject} from "./usana-object";
import {TranslateService} from "./translation/translate.service";
import {AnalyticsService} from "./analytics/analytics.service";
import {UsanaCookieService} from "./common/usana-cookie.service";
import {GqlShareListService} from "./social/share-list/gql-share-list.service";
import {UsanaMarketService} from "@usana/ux/markets";
import {ScrollRestorationService} from "./common/scroll-restoration.service";
import {ReferralUrlService} from "./referral-redirect/referral-url.service";
import {EmailVerificationUrlService} from "./email-verification/email-verification-url.service";

// Top-level path segments served by the cart router. Derived from the route config so
// this set stays in sync automatically when routes are added or removed.
export const CART_PATH_SEGMENTS: Set<string> = new Set([
    ...cartUxLocaleRoutes
        .map(r => r.path?.split('/')[0])
        .filter((s): s is string => !!s && s !== '' && s !== '**'),
    ...REDIRECT_URLS.map(r => r.path.split('/')[0]),
]);

// Top-level path segments that live outside /ux/* and should bypass all URL-prefix
// modification in modifyUrl (neither cart nor dotcom prefixing applies to them).
const ROOT_PATH_SEGMENTS: Set<string> = new Set(
    rootAppRoutes
        .map(r => r.path?.split('/')[0])
        .filter((s): s is string => !!s && s !== '' && s !== '**')
);

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    standalone: false
})
export class AppComponent extends UsanaObject implements OnInit, OnDestroy {

    userLocale;
    @ViewChild('bottomMenu', {read: ViewContainerRef}) private bottomMenuRef: ViewContainerRef;

    private subscriptions: Subscription = new Subscription();

    constructor(
        private router: Router,
        private sessionService: SessionService,
        private translateService: TranslateService,
        private userService: UserService,
        private cartService: CartService,
        private container: ViewContainerRef,
        private customerCacheService: CustomerCacheService,
        private welcomeModalService: WelcomeModalService,
        private gqlShopSessionService: GqlShopSessionService,
        private analyticsService: AnalyticsService,
        private usanaCookieService: UsanaCookieService,
        private navigationService: NavigationService,
        private gqlShareListService: GqlShareListService,
        private usanaMarketService: UsanaMarketService,
        private referralUrlService: ReferralUrlService,
        private emailVerificationUrlService: EmailVerificationUrlService,
        private scrollRestorationService: ScrollRestorationService,
        loggerService: LoggerService,
    ) {
        super(loggerService);
    }

    ngOnInit(): void {
        this.scrollRestorationService.init();

        this.subscriptions.add(
            this.sessionService.fetchShopUser()
                .pipe(
                    tap(() => this.shopSessionEventBrowse()),
                    switchMap(() => this.cartService.fetchCart()),
                    switchMap(() => this.sessionService.retrieveCustomer(false)),
                    switchMap(() => this.gqlShareListService.getShareLists())
                )
                .subscribe()
        );

        this.referralUrlService.init();
        this.emailVerificationUrlService.init();

        if (Util.isMobile()) {
            this.loadBottomMenuComponent();
        }

        this.subscriptions.add(
            // Re-fire on each i18nInfo emission so the correction lands once the
            // backend market list arrives — defeats a startup race for markets
            // missing from the @usana/ux/markets hardcoded preload (e.g. enu-NL).
            // setMarketId is idempotent.
            combineLatest([
                this.sessionService.usanaLocale$,
                this.usanaMarketService.geti18NInfoSubject(),
            ]).subscribe(([locale]) => {
                if (locale) {
                    this.userLocale = locale;
                    this.correctMarketToSessionLocale();
                }
            })
        );

        this.subscriptions.add(
            this.router.events.pipe(
                filter((event) => event instanceof NavigationStart)
            ).subscribe((event: NavigationStart) => {
                this.navigationService.navigationStartSubject.update(event);
                if (!this.userLocale) {
                    //This code only runs on a refresh when the NavigationStart event fires before a user with a locale is populated
                    this.subscriptions.add(
                        this.userService.getShopUser().subscribe((user: IShopUser) => this.modifyUrl(event.url))
                    );
                } else {
                    this.modifyUrl(event.url);
                }
            })
        );

        this.subscriptions.add(
            this.customerCacheService.customerSubject.watch().subscribe(customer => {
                const validState = !ShareableUtil.isShareableState() && !Util.isEnrollState() && !Util.isCheckoutState();
                if (validState && customer?.config.freeShippingEligible && customer?.config.firstOrder) {
                    this.welcomeModalService.open(customer.customerType.code);
                }
            })
        );

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                this.sessionService.checkForSessionChange();
            }
        });
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    @HostListener('window:beforeunload', ['$event'])
    beforeUnload() {
        if (Util.isSignupState() && this.translateService.alternateLocaleSubject.isPresent()) {
            this.translateService.resetAlternateLocale();
        }
    }

    // Main method to modify the URL
    modifyUrl(eventUrl: string) {
        //if userLocale error OR Prerender, this will stop infinite loops
        if (!this.userLocale) {
            return;
        }

        const origUrl = decodeURIComponent(eventUrl);
        // remove the hashbang from the URL:
        //   - leading:           #!/ux/cart/...  or  /#/ux/cart/...
        //   - slash-surrounded:  /ux/cart/#!/en-US/...  (hash between two slashes)
        //   - Angular-normalized:/ux/cart#!/en-US/...   (Angular strips the trailing
        //                        slash before # when it re-serializes the URL)
        const newUrl = origUrl
            .replace(/^(?:\/#)?#?!?/, '')
            .replace(/\/#!?\//, '/')
            .replace(/(cart|dotcom)#!?\//, '$1/');
        if (origUrl !== newUrl) {
            this.log.debug('modifyUrl', 'hashbang stripped, redirecting', origUrl, '->', newUrl);
            this.router.navigateByUrl(newUrl);
        } else {
            this.log.debug('modifyUrl', 'no redirect', origUrl);
            this.continueUrlChecks(eventUrl);
        }
    }

    // Set locale in the URL if necessary, handles redirects and default page (/ux/cart/:locale/...)
    private continueUrlChecks(url: string) {
        // NavigationStart.url carries the query string / fragment, and splitting on '/' glues it
        // onto the final path segment ("/ux/cart?utm_source=x" -> [..., "cart?utm_source=x"]),
        // which breaks every segment comparison below — most visibly the /ux/cart prefix guard,
        // sending bare marketing links to /ux/dotcom/:locale/ux/cart (DEV-97405). Analyze the
        // path alone and re-attach the suffix when navigating.
        const suffixIndex = url.search(/[?#]/);
        const urlArr = (suffixIndex === -1 ? url : url.slice(0, suffixIndex)).split('/');
        const suffix = suffixIndex === -1 ? '' : url.slice(suffixIndex);

        // Stripe's Apple Pay QR return URL (/ux/cart/payment/wallet/:result) opens on the customer's phone,
        // which has no locale or session; the locale logic below would otherwise corrupt the path.
        const isWalletResult: boolean = urlArr[1] === 'ux' && urlArr[2] === 'cart' && urlArr[3] === Constants.PAYMENT_CALLBACK_STATE && urlArr[4] === 'wallet';

        // Root-level routes (e.g. /r/:code) live outside /ux/* — pass through untouched.
        if (ROOT_PATH_SEGMENTS.has(urlArr[1]) || isWalletResult) {
            return;
        }
        let urlModified = this.isKnownCartPath(urlArr)
            ? LocaleUtil.prependUxCartPrefixIfNeeded(urlArr)
            : LocaleUtil.prependUxDotcomPrefixIfNeeded(urlArr);
        const isDotcomArea = urlArr[1] === 'ux' && urlArr[2] === 'dotcom';
        if (urlArr[Constants.URL_LOCALE_INDEX] != this.userLocale) {
            //if url has locale prefix but is not the current locale, replace locale with current
            LocaleUtil.replaceLocale(urlArr, this.userLocale);
            urlModified = true;
        }

        if (isDotcomArea) {
            // Dotcom routing (default page, CMS vs non-CMS) is handled by DOTCOM_ROUTES.
            // Only apply the locale replacement above; skip all cart-specific checks below.
            this.navigateIfModified(urlModified, urlArr, suffix);
            return;
        }

        if(this.shouldRedirect(urlArr)) {
            this.changeToRedirectUrl(urlArr);
            urlModified = true;
        }

        //if url consists of ONLY the locale, reroute to /locale/landing state
        if (urlArr[urlArr.length - 1] == this.userLocale) {
            urlArr.push(Constants.LANDING_STATE);
            urlModified = true;
        }

        //if route was modified to fit above requirements, reroute.  Else do nothing because route is correct
        this.navigateIfModified(urlModified, urlArr, suffix);
    }

    /**
     * Returns true for paths whose first meaningful segment matches a known cart route
     * (e.g. /signup/pc, /autoOrder, /zh-CN/checkout). These are sent to /ux/cart rather
     * than /ux/dotcom. Paths already under /ux/* pass through unchanged via the prefix
     * guard inside prependUxDotcomPrefixIfNeeded.
     */
    private isKnownCartPath(urlArr: string[]): boolean {
        // Skip a leading locale segment if present (e.g. /zh-CN/signup/pc)
        let idx = 1;
        if (LocaleUtil.validLocale(urlArr[idx]) || LocaleUtil.isLegacyLocaleSegment(urlArr[idx])) {
            idx = 2;
        }
        return CART_PATH_SEGMENTS.has(urlArr[idx]);
    }

    // suffix is the query string / fragment stripped off by continueUrlChecks, re-attached here.
    navigateIfModified(urlModified: boolean, urlArr: string[], suffix: string = '') {
        if (urlModified) {
            this.router.navigateByUrl(urlArr.join('/') + suffix);
        }
    }

    shouldRedirect(urlArr: string[]) {
        //urlArr is query-param free — continueUrlChecks strips the suffix before splitting
        const urlArrayNoLocale = urlArr.slice(Constants.URL_LOCALE_INDEX + 1);
        return _.find(REDIRECT_URLS, {path: urlArrayNoLocale.join('/')});
    }

    //This function needs to be called after we have added the locale to the URL
    changeToRedirectUrl(urlArr: string[]) {
        //takes off the prefix + locale ex: ["", "ux", "cart", "en-US"]
        const urlArrayLocale = urlArr.slice(0, Constants.URL_LOCALE_INDEX + 1);
        //take the remainder of url ex: ["signup","pc","info"]
        const urlArrayNoLocale = urlArr.slice(Constants.URL_LOCALE_INDEX + 1);
        //look up the redirect non-locale section in the list ex: "signup/pc/info"
        const redirectTo = _.find(REDIRECT_URLS, {path: urlArrayNoLocale.join('/')})?.redirectTo;
        //replace the urlArr passed in with the new redirect URL
        urlArr.length = 0; //clear the array
        urlArr.push(...urlArrayLocale); //add the locale section
        urlArr.push(...redirectTo.split('/'));  //add the rest of the url redirect section
    }

    /**
     * Aligns UsanaMarketService with the backend-authoritative session locale. Called from the
     * usanaLocale$ subscription so it fires as soon as populateShopUser() returns — before any
     * navigation event and before components render. Also fires on any subsequent locale change
     * (e.g. locale switcher). The library's appManagesCurrent flag prevents the async backend
     * i18nInfo response from overwriting this value.
     */
    private correctMarketToSessionLocale(): void {
        const expectedMarketId = LocaleUtil.getDotcomLocaleLanguage3(
            LocaleUtil.getUsanaLocale(this.userLocale));
        this.log.debug('correctMarketToSessionLocale', 'setting market to:', expectedMarketId);
        this.usanaMarketService.setMarketId(expectedMarketId);
    }

    async loadBottomMenuComponent() {
        this.container.clear();
        const {BottomMenuComponent} = await import('../modules/bottom-menu/bottom-menu.component');
        this.bottomMenuRef.createComponent(BottomMenuComponent);
    }

    shopSessionEventBrowse() {
        if(!Util.isPrerender(window)) {
            setTimeout(()=> {
                this.gqlShopSessionService.saveFirstShopSessionEvent().subscribe(() => {
                    this.analyticsService.loadShopEvent(this.usanaCookieService.getCookie(Constants.SHOP_SESSION_ID_COOKIE));
                });
            }, Constants.SHOP_SESSION_EVENT_BROWSE_DELAY);
        }
    }
}

